import React, { useState, useEffect } from 'react';
import { useToneGame } from './ToneGameContext';

export const ToneHUD: React.FC = () => {
  const { state } = useToneGame();
  const { gamePhase, currentRound, sequence, playerSequence, stats, levelStartTime } = state;
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for live timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate current stats
  const sequenceLength = sequence.length;
  const playerProgress = playerSequence.length;
  const progress = gamePhase === 'repeating' ? `${playerProgress} / ${sequenceLength}` : `${sequenceLength}`;
  
  // Calculate level time
  const levelTime = levelStartTime ? Math.floor((currentTime - levelStartTime) / 1000) : 0;
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Get next tone to play
  const getNextTone = () => {
    if (gamePhase === 'repeating' && playerProgress < sequenceLength) {
      return sequence[playerProgress]?.note || '';
    }
    return '';
  };

  const getPhaseDisplay = () => {
    switch (gamePhase) {
      case 'ready':
        return `Ready for Round ${currentRound}`;
      case 'listening':
        return `Playing Sequence (Round ${currentRound})`;
      case 'repeating':
        return `Repeat the Sequence`;
      case 'complete':
        return `Round ${currentRound} Complete!`;
      case 'feedback':
        return `Checking your sequence...`;
      default:
        return 'Tone Memory';
    }
  };

  const getAccuracy = () => {
    if (stats.completedSequences === 0) return 0;
    return ((stats.completedSequences / currentRound) * 100).toFixed(1);
  };

  const getAverageReactionTime = () => {
    if (stats.reactionTimes.length === 0) return 0;
    return Math.round(stats.reactionTimes.reduce((a, b) => a + b, 0) / stats.reactionTimes.length);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      {/* Header with current objective */}
      <div className="text-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          {getPhaseDisplay()}
        </h2>
        {gamePhase === 'repeating' && getNextTone() && (
          <div className="text-2xl font-bold text-blue-600">
            Next Tone: {getNextTone()}
          </div>
        )}
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
        <div className="text-center">
          <div className="text-sm text-gray-600">Round:</div>
          <div className="text-xl font-bold text-blue-600">
            {currentRound}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Level Time:</div>
          <div className="text-xl font-bold text-orange-600">
            {formatTime(levelTime)}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Sequence:</div>
          <div className="text-xl font-bold text-purple-600">
            {sequenceLength} tones
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Progress:</div>
          <div className="text-xl font-bold text-green-600">
            {progress}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Errors:</div>
          <div className="text-xl font-bold text-red-600">
            {stats.errorsPerRound.reduce((total, errors) => total + errors, 0)}
          </div>
        </div>
      </div>

      {/* Progress Bar for Repeating Phase */}
      {gamePhase === 'repeating' && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Sequence Progress</span>
            <span>{Math.round((playerProgress / sequenceLength) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(playerProgress / sequenceLength) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Performance Stats */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
          Performance Statistics
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-gray-600">Completed:</div>
            <div className="font-semibold text-green-600">{stats.completedSequences}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600">Success Rate:</div>
            <div className="font-semibold text-blue-600">{getAccuracy()}%</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600">Avg Response:</div>
            <div className="font-semibold text-purple-600">{getAverageReactionTime()}ms</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600">Game Time:</div>
            <div className="font-semibold text-orange-600">
              {currentRound * 30}s
            </div>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-700 text-center">
          {gamePhase === 'ready' && (
            <span>🎵 Click "Start Round" to begin listening to the sequence</span>
          )}
          {gamePhase === 'listening' && (
            <span>👂 Listen carefully to the tone sequence</span>
          )}
          {gamePhase === 'repeating' && (
            <span>🎮 Click the colored pads to repeat the sequence</span>
          )}
          {gamePhase === 'feedback' && (
            <span>⏳ Checking your sequence...</span>
          )}
          {gamePhase === 'complete' && (
            <span>✅ Great job! Ready for the next round?</span>
          )}
        </div>
      </div>
    </div>
  );
};